import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, ArrowsClockwise, UploadSimple, CheckCircle, WarningCircle, Trash, Broom, CloudArrowUp, Globe } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  queued: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  wiping: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  purging: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  done: 'bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/30',
  failed: 'bg-red-500/10 text-red-400 border-red-500/30',
};

const DOBulkOverride = () => {
  const [deployments, setDeployments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const fetchDeployments = async () => {
    try {
      const res = await axios.get(`${API}/api/deployments`, { withCredentials: true });
      setDeployments(res.data.filter(d => d.status === 'live' || d.override_status));
    } catch (e) {
      toast.error('Failed to load deployments');
    }
  };

  useEffect(() => {
    fetchDeployments();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const liveIds = deployments.filter(d => d.status === 'live').map(d => d.id);
  const allSelected = liveIds.length > 0 && liveIds.every(id => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(liveIds));
  };

  const startPolling = () => {
    setRunning(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      await fetchDeployments();
      const res = await axios.get(`${API}/api/deployments`, { withCredentials: true });
      const targets = res.data.filter(d => selected.has(d.id));
      const allDone = targets.length > 0 && targets.every(d => ['done', 'failed'].includes(d.override_status));
      if (allDone) {
        clearInterval(pollRef.current);
        setRunning(false);
        const ok = targets.filter(d => d.override_status === 'done').length;
        const fail = targets.filter(d => d.override_status === 'failed').length;
        toast.success(`Override complete: ${ok} live, ${fail} failed`);
      }
    }, 4000);
  };

  const runOverride = async () => {
    if (selected.size === 0) return toast.error('Select at least one URL');
    if (!file) return toast.error('Upload a .zip file first');
    if (!window.confirm(`This will WIPE ALL FILES in ${selected.size} selected site(s), upload the new ZIP, and purge the CDN 3×. This cannot be undone. Continue?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('deployment_ids', Array.from(selected).join(','));
      fd.append('file', file);
      const res = await axios.post(`${API}/api/deployments/bulk-override`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Override started for ${res.data.count} site(s)`);
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start override');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2 text-primary">BULK OVERRIDE</h1>
        <p className="text-slate-400 font-mono">Select live Spaces URLs, upload a fresh ZIP → we wipe every file, upload the new site, and purge the CDN 3×.</p>
      </div>

      {/* Upload + action bar */}
      <div className="bg-card border border-primary/30 p-6 flex flex-col lg:flex-row gap-4 lg:items-center">
        <label className="flex-1 flex items-center gap-3 border-2 border-dashed border-white/15 hover:border-primary transition-colors p-4 cursor-pointer">
          <UploadSimple size={24} className="text-primary shrink-0" />
          <span className="text-sm text-slate-300 truncate">{file ? file.name : 'Click to select the new website .zip (index.html at root)'}</span>
          <input type="file" accept=".zip" className="hidden" data-testid="bulk-override-zip-input" onChange={(e) => setFile(e.target.files[0])} />
        </label>
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-400 font-mono">{selected.size} selected</div>
          <button onClick={runOverride} disabled={busy || running || selected.size === 0 || !file} data-testid="run-bulk-override-btn"
            className="flex items-center gap-2 bg-primary text-black px-6 py-3 font-bold uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-40">
            {busy || running ? <CircleNotch size={20} className="animate-spin" /> : <Broom size={20} />}
            {running ? 'Overriding…' : 'Wipe & Redeploy + Purge 3×'}
          </button>
        </div>
      </div>

      {/* Deployments table */}
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/20">
              <th className="p-4 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-checkbox" className="accent-primary w-4 h-4" />
              </th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Bucket</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Account (DC)</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Override</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map(dep => {
              let finalUrl = dep.public_url || '';
              if (finalUrl && !finalUrl.startsWith('http')) finalUrl = 'https://' + finalUrl;
              const isLive = dep.status === 'live';
              return (
                <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <input type="checkbox" disabled={!isLive} checked={selected.has(dep.id)} onChange={() => toggle(dep.id)}
                      data-testid={`select-dep-${dep.id}`} className="accent-primary w-4 h-4 disabled:opacity-30" />
                  </td>
                  <td className="p-4 font-mono text-sm text-white">{dep.bucket_name}</td>
                  <td className="p-4 text-sm">{dep.account_label} <span className="text-slate-500 uppercase">({dep.datacenter})</span></td>
                  <td className="p-4 text-sm">
                    {finalUrl ? (
                      <a href={finalUrl} target="_blank" rel="noreferrer" className="text-[#00E5FF] text-xs hover:underline flex items-center gap-1 font-mono truncate max-w-xs" title={finalUrl}>
                        <Globe size={13} /> {finalUrl}
                      </a>
                    ) : '-'}
                  </td>
                  <td className="p-4 text-sm">
                    {dep.override_status ? (
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs font-bold tracking-wider border w-fit ${STATUS_STYLE[dep.override_status] || 'bg-white/5 text-slate-300 border-white/10'}`}>
                          {dep.override_status}
                        </span>
                        {dep.override_status === 'done' && (
                          <span className="text-[10px] text-slate-500 font-mono">wiped {dep.override_wiped} · uploaded {dep.override_uploaded} · purged {dep.override_purges}×</span>
                        )}
                        {dep.override_status === 'failed' && (
                          <span className="text-[10px] text-red-400 font-mono max-w-xs truncate" title={dep.override_error}>{dep.override_error}</span>
                        )}
                      </div>
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
            {deployments.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-slate-500">No live deployments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DOBulkOverride;
